import * as React from 'react';

import {RqlQueryResult} from './useQuery';
import {MonacoToken, QueryMonacoEditorTextInput} from './QueryInput';
import {Loading} from './Loading';
import {TabbedQueryResults, QueryError} from './QueryResults';
import {TranslationContext} from './commons';

export const RqlBrowserQuerier: React.FC<{
    query: string;
    setQuery: (input: string) => void;
    resultsAndVariables: {
        results: RqlQueryResult | null;
        variables: string[] | null;
    };
    executeQuery: () => void;
    queryIsLoading: boolean;
    setCursorToken: (token: MonacoToken) => void;
    isForbiddenQuery: boolean;
}> = ({
    query,
    setQuery,
    resultsAndVariables,
    executeQuery,
    queryIsLoading,
    setCursorToken,
    isForbiddenQuery,
}) => {
    let resultsDisplay = null;
    const {results, variables} = resultsAndVariables;

    if (queryIsLoading) {
        resultsDisplay = <Loading />;
    } else if (results) {
        if (results.status === 'success') {
            resultsDisplay = (
                <TabbedQueryResults
                    rows={results.rows}
                    queryVariables={variables}
                />
            );
        } else {
            resultsDisplay = <QueryError reason={results.reason} />;
        }
    }

    let buttonDisplay = (
        <button className="execute-button" onClick={executeQuery}>
            ▶
        </button>
    );
    if (isForbiddenQuery) {
        buttonDisplay = (
            <TranslationContext.Consumer>
                {(tc) => (
                    <button className="execute-button execute-button-disabled">
                        <div className="readonly-tooltip">
                            ▶
                            <span className="tooltiptext">
                                {tc.translate(
                                    'No INSERT, SET or DELETE allowed in the query'
                                )}
                            </span>
                        </div>
                    </button>
                )}
            </TranslationContext.Consumer>
        );
    }

    return (
        <div className="rql-querier">
            <div className="rql-querier-sub-container">
                <div className="rql-text-editor">
                    <QueryMonacoEditorTextInput
                        query={query}
                        onQueryChange={setQuery}
                        executeQuery={executeQuery}
                        setCursorToken={setCursorToken}
                    />
                </div>
                {buttonDisplay}
            </div>
            {resultsDisplay}
        </div>
    );
};

export const RqlBrowserControlPanel: React.FC<{
    showMetamodel: boolean;
    setMetamodel: (b: boolean) => void;
    autoHelp: boolean;
    setAutoHelp: (b: boolean) => void;
    setEntityTypeTextSearch: (str: string) => void;
    setLinkedEntityTypeTextSearch: (str: string) => void;
    setRelationsAndAttributesTextSearch: (str: string) => void;
}> = ({
    showMetamodel,
    setMetamodel,
    autoHelp,
    setAutoHelp,
    setEntityTypeTextSearch,
    setLinkedEntityTypeTextSearch,
    setRelationsAndAttributesTextSearch,
}) => {
    return (
        <TranslationContext.Consumer>
            {(tc) => (
                <div className="control-panel">
                    <div className="control-option">
                        <p className="option-name">
                            {tc.translate('Show Metamodel')}
                        </p>
                        <label className="checkbox">
                            <input
                                type="checkbox"
                                checked={showMetamodel}
                                onChange={() => setMetamodel(!showMetamodel)}
                            />
                            <span className="checked-box" />
                        </label>
                    </div>
                    <div className="control-option">
                        <p className="option-name">
                            {tc.translate('Auto help')}
                        </p>
                        <label className="checkbox">
                            <input
                                type="checkbox"
                                checked={autoHelp}
                                onChange={() => setAutoHelp(!autoHelp)}
                            />
                            <span className="checked-box" />
                        </label>
                    </div>
                    <div className="control-option">
                        <button
                            className="option-button"
                            onClick={() => {
                                setEntityTypeTextSearch('');
                                setLinkedEntityTypeTextSearch('');
                                setRelationsAndAttributesTextSearch('');
                            }}
                        >
                            {tc.translate('Clear Selection')}
                        </button>
                    </div>
                </div>
            )}
        </TranslationContext.Consumer>
    );
};

export const RqlBrowserError: React.FC = () => (
    <TranslationContext.Consumer>
        {(tc) => (
            <div className="rql-browser">
                <div className="rql-browser-error-container">
                    <div className="rql-browser-error">
                        {tc.translate(
                            'There are connection issues with the CubicWeb' +
                                ' instance you entered, please check:'
                        )}
                        <ul>
                            <li>{tc.translate('That the URL is correct')}</li>
                            <li>
                                {tc.translate(
                                    'That you have access to this' +
                                        ' CubicWeb instance'
                                )}
                            </li>
                            <li>
                                {tc.translate(
                                    'That browser privacy extensions' +
                                        ' are not interfering'
                                )}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        )}
    </TranslationContext.Consumer>
);
